import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { useQueryClient } from '@tanstack/react-query';
import { format, isWithinInterval, startOfMonth, endOfMonth, startOfYear, endOfYear, isSameMonth } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { User, FilterIcon, CheckIcon, ArrowDown, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BASE_URL } from '@/config/api';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft, Calendar as CalendarIcon, CreditCard, Download, FileText, Save, Trash, AlertCircle, Plus, Info
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateRange as DayPickerDateRange } from 'react-day-picker';
import { DateRange as RDPDateRange } from 'react-day-picker';



interface EventItem {
  id: string;
  date: string;
  fullDay: boolean;
  category: string;
  categoryId: string;
  categoryColor: string;
  title:string;
  duration: number;
  studentCount: number;
  price: number;
  extraCharge?: number;
  description: string;
  notes: string;
  selected: boolean;
}

interface TaxItem {
  id: string;
  name: string;
  rate: number;
  selected: boolean;
}

type AdditionalFee = {
  description: string;
  amount: number;      // pour les calculs
  amountInput?: string; // affichage brut dans l’input
};

interface Student {
  firstname: string;
  lastname: string;
  status: string;
  invoice_split_between_parents?: boolean;
}

type DateRange = Partial<RDPDateRange>;

type FilterPeriod = 'all' | 'currentMonth' | 'currentYear' | 'custom';

const additionalFeeSchema = z.object({
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().min(0.01, { message: "Amount must be greater than 0" })
});



const invoiceSchema = z.object({
  invoiceNumber: z.string().optional(),
  invoiceDate: z.date(),
  dueDate: z.date(),
  discountType: z.enum(['none', 'percentage', 'amount']).default('none'),
  discountValue: z.number().min(0).default(0),
  note: z.string().optional(),
});

const InvoiceCreate = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pendingEvents, setPendingEvents] = useState<EventItem[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventItem[]>([]);
  const [taxes, setTaxes] = useState<TaxItem[]>([]);
  const [discountType, setDiscountType] = useState<'none' | 'percentage' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountInput, setDiscountInput] = useState('');
  const [discountScope, setDiscountScope] = useState<'all' | 'lessons' | 'events'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [billingPeriodRange, setBillingPeriodRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [billingCalendarMonth, setBillingCalendarMonth] = useState<Date>(new Date());
  const [categories, setCategories] = useState<any[]>([]);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  const [studentLoading, setIsStudentLoading] = useState<boolean>(false);
  const [student, setStudent] = useState<Student | null>(null);
  const [additionalFees, setAdditionalFees] = useState<AdditionalFee[]>([]);
  const isMobile = useIsMobile();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const dueDatePreference = Number(user.teacher.due_date_preference || 7); 
  const isDisplayStudentCount = Boolean(user.teacher.display_student_count);
  const taxeType = Number(user?.teacher?.taxe_type) === 1 ? 1 : 0;
  const [invoiceDateMonth, setInvoiceDateMonth] = useState<Date>(new Date());
  const [dueDateMonth, setDueDateMonth] = useState<Date>(new Date());

  const queryClient = useQueryClient();

  // Calculate the due date by adding the specified number of days to the current date
  const calculateDueDate = () => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + dueDatePreference); // Add days
    return dueDate;
  };

  const addAdditionalFee = () => {
  setAdditionalFees([
    ...additionalFees,
    { description: "", amount: 0 }
  ]);
  };

  const removeAdditionalFee = (index: number) => {
    setAdditionalFees(additionalFees.filter((_, i) => i !== index));
  };

  const sortEventsByDate = (events: EventItem[]) => {
  return [...events].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });
};


 const handleSortChange = () => {
  const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
  setSortOrder(newSortOrder);
  setFilteredEvents(sortEventsByDate(filteredEvents));
};


const handleAdditionalFeeChange = (
  index: number,
  field: keyof AdditionalFee,
  value: string | number
) => {
  const updatedFees = [...additionalFees];

  if (field === 'description') {
    updatedFees[index].description = value as string;
  } else if (field === 'amount') {
    const raw = String(value);

    // mise à jour de l'affichage brut
    updatedFees[index].amountInput = raw;

    // conversion pour la logique
    const num = parseFloat(raw.replace(',', '.'));
    updatedFees[index].amount = isNaN(num) ? 0 : num;
  }

  setAdditionalFees(updatedFees);
};


  const getAdditionalFeesTotal = () => {
    return additionalFees.reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  };

  const form = useForm<z.infer<typeof invoiceSchema>>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: `INV-${Date.now().toString().substring(7)}`, // Valeur par défaut
      invoiceDate: new Date(),
      dueDate: calculateDueDate(),
      discountType: 'none',
      discountValue: 0,
      note: '',
    },
  });

  

  /*const { data: student, isLoading: studentLoading, error: studentError } = useQuery({
    queryKey: ['student', studentId, token],
    queryFn: async () => {
      const students = await eventService.getStudents(studentId, token);
      return students.find((s: any) => s.id == studentId) || null;
    },
  });*/

  const { data: eventData, isLoading: eventsLoading } = useQuery({
    queryKey: ['billingEvents', studentId, token],
    queryFn: async () => {
      try {
        setIsStudentLoading(true);
        const response = await fetch(`${BASE_URL}/api/billing/events/${studentId}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
           'Accept' : 'application/json',
           'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch billing events');
        }

        const data = await response.json();
        setStudent(data.student);
        //console.log('Fetched billing events:', data);
        return data.events || [];
      } catch (error) {
        console.error('Error fetching billing events:', error);
        return [];
      }
      finally {
        setIsStudentLoading(false);
      }
    },
  });

  const { data: taxData, isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes', token],
    queryFn: async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/billing_taxes`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Accept' : 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch taxes');
        }

        const data = await response.json();
        return data.taxes || [];
      } catch (error) {
        console.error('Error fetching taxes:', error);
        return [];
      }
    },
  });

    const removeAccents = (str) => {
      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };

    const invoiceDate = form.watch("invoiceDate");

    useEffect(() => {
      if (student && invoiceDate) {
        let month = invoiceDate.toLocaleString('default', { month: 'short' });
        month = removeAccents(month).replace('.', '');

        const year = invoiceDate.getFullYear();

        // Vérifier si le prénom et le nom de famille existent et ne sont pas vides
        let newInvoiceNumber;
        if (student.firstname && student.firstname.trim() !== '') {
          const firstName = student.firstname.toLowerCase().replace(/\s+/g, '');
          const lastName = student.lastname.toLowerCase().replace(/\s+/g, '');
          newInvoiceNumber = `${firstName}-${lastName}-${month}-${year}`;
        } else if (student.lastname && student.lastname.trim() !== '') {
          const lastName = student.lastname.toLowerCase().replace(/\s+/g, '');
          newInvoiceNumber = `${lastName}-${month}-${year}`;
        } else {
          // Gérer le cas où ni le prénom ni le nom de famille ne sont disponibles
          newInvoiceNumber = `${month}-${year}`;
        }

        form.setValue('invoiceNumber', newInvoiceNumber);
      }
    }, [student, invoiceDate, form]);




  useEffect(() => {
    if (eventData) {
      //console.log('Event data:', eventData);

      // Extract the values from the object into an array
      const eventsArray = Object.values(eventData);

      //console.log('Number of events:', eventsArray.length);

      const formattedEvents = eventsArray.map((event:any) => {
        //console.log('Event:', event);
        return {
          id: event.id,
          date: event.date || '', // Use the date directly since it's already a string
          category: event.category || 'Lesson',
          categoryId: event.categoryId || 4,
          studentCount: event.studentCount || 0,
          fullDay: event.fullDay || false,
          categoryColor: event.categoryColor || '#3498db',
          duration: event.duration || 60,
          price: event.price || 0,
          extraCharge: event.extraCharge || 0,
          description: event.description || 'Lesson',
          notes: event.notes || '',
          title: event.title || 'Default Title',
          selected: true,
        };
      });

      //console.log('Formatted events:', formattedEvents);

      setPendingEvents(formattedEvents);
      setFilteredEvents(formattedEvents);
    } else {
      //console.log('eventData is undefined or null');
    }
  }, [eventData]);

  

  useEffect(() => {
    if (taxData) {
      const formattedTaxes = taxData.map((tax: any) => ({
        id: tax.id,
        name: tax.name,
        rate: tax.rate,
        tax_number: tax.tax_number || '',
        selected: tax.selected || tax.id === '1',
        amount: 0, // Initialisez le montant à 0
      }));
      setTaxes(formattedTaxes);
    }
  }, [taxData]);

  const selectedTaxesKey = taxes.map((tax) => tax.selected).join(',');

  useEffect(() => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;

    setTaxes((prevTaxes) =>
      prevTaxes.map((tax) => ({
        ...tax,
        amount: tax.selected
          ? (taxeType === 1
              ? taxableAmount - taxableAmount / (1 + tax.rate / 100)
              : (taxableAmount * tax.rate) / 100)
          : 0,
      }))
    );
  }, [filteredEvents, selectedTaxesKey, discountType, discountValue, discountScope, taxeType]);

  useEffect(() => {
    if (pendingEvents.length > 0) {
      const uniqueCategories = Array.from(
        new Set(pendingEvents.map(event => event.categoryId))
      ).map(categoryId => {
        const event = pendingEvents.find(event => event.categoryId === categoryId);
        return {
          id: categoryId,
          name: event?.category || '',
          color: event?.categoryColor || '#3498db'
        };
      });
      setCategories(uniqueCategories);
    }
  }, [pendingEvents]);

  useEffect(() => {
    if (filterPeriod === 'all') {
      if (pendingEvents.length > 0) {
        const dates = pendingEvents.map(e => new Date(e.date));
        const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
        setDateRange({ from: minDate, to: maxDate });
      }
    } else if (filterPeriod === 'currentMonth') {
      const now = new Date();
      setDateRange({ from: startOfMonth(now), to: endOfMonth(now) });
    } else if (filterPeriod === 'currentYear') {
      const now = new Date();
      setDateRange({ from: startOfYear(now), to: endOfYear(now) });
    }
    // For 'custom', the date range is set by the user, so we don't do anything here.
  }, [filterPeriod, pendingEvents]);

  useEffect(() => {
    applyFilters();
  }, [filterCategory, dateRange, pendingEvents]);

  const applyFilters = () => {
    let filtered = [...pendingEvents];

    if (filterCategory !== 'all') {
      filtered = filtered.filter(event => event.categoryId === filterCategory);
    }

    if (dateRange.from && dateRange.to) {
      // Ensure the whole day is included in the interval
      const start = new Date(dateRange.from);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateRange.to);
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.date);
        return isWithinInterval(eventDate, { start, end });
      });
    }

    setFilteredEvents(filtered);
  };

  useEffect(() => {
    if (pendingEvents.length > 0) {
        const mostRecentEvent = pendingEvents.reduce((latest, event) => 
            new Date(event.date) > new Date(latest.date) ? event : latest
        );
        const mostRecentDate = new Date(mostRecentEvent.date);
        const firstDay = startOfMonth(mostRecentDate);
        const lastDay = endOfMonth(mostRecentDate);

        setBillingPeriodRange({ from: firstDay, to: lastDay });
        setBillingCalendarMonth(firstDay);
    }
  }, [pendingEvents]);

  const resetFilters = () => {
    setFilterCategory('all');
    setFilterPeriod('all');
    setDateRange({ from: undefined, to: undefined });
    setFilteredEvents(pendingEvents);
  };

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || '$'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(date);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins}min`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}min`;
    }
  };

  const handleToggleEvent = (eventId: string) => {
    setPendingEvents((prev) =>
      prev.map((event) =>
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
    setFilteredEvents((prev) =>
      prev.map((event) =>
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
  };

  const handleToggleTax = (taxId: string) => {
    setTaxes((prev) =>
      prev.map((tax) =>
        tax.id === taxId ? { ...tax, selected: !tax.selected } : tax
      )
    );
  };

  const getSubtotalWithExtra = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.extraCharge, 0);
  };

  const getSubtotal = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.price, 0);
  };

  const getExtraCharges = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.extraCharge, 0);
  };

  const isLessonEvent = (event: EventItem) => event.title === 'Lesson';

  const getScopedSubtotal = () => {
    const selectedEvents = filteredEvents.filter((event) => event.selected);

    if (discountScope === 'lessons') {
      return selectedEvents
        .filter(isLessonEvent)
        .reduce((sum, event) => sum + event.price, 0);
    }

    if (discountScope === 'events') {
      return selectedEvents
        .filter((event) => !isLessonEvent(event))
        .reduce((sum, event) => sum + event.price, 0);
    }

    return selectedEvents.reduce((sum, event) => sum + event.price, 0);
  };

  const getDiscountAmount = () => {
    const subtotal = getScopedSubtotal();

    if (discountType === 'none' || discountValue === 0) {
      return 0;
    } else if (discountType === 'percentage') {
      return (subtotal * discountValue) / 100;
    } else {
      return Math.min(discountValue, subtotal);
    }
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;

    return taxes
      .filter((tax) => tax.selected)
      .reduce((sum, tax) => sum + (taxeType === 1
        ? taxableAmount - taxableAmount / (1 + tax.rate / 100)
        : (taxableAmount * tax.rate) / 100), 0);
  };

  const getTotal = () => {
  const subtotal = getSubtotal();
  const discount = getDiscountAmount();
  const tax = getTaxAmount();
  const fees = getAdditionalFeesTotal();
  const extraCharges = getExtraCharges();

  // taxeType === 1 : la taxe est déjà incluse dans les prix (TTC), on ne l'ajoute pas
  return subtotal - discount + (taxeType === 1 ? 0 : tax) + fees + extraCharges;
};

  const onSubmit = async (values: z.infer<typeof invoiceSchema>) => {
    const selectedEvents = filteredEvents.filter((event) => event.selected);

    if (selectedEvents.length === 0) {
      toast({
        variant: "destructive",
        title: translateStr('invoices.noEventsSelected'),
        description: translateStr('invoices.pleaseSelectEvents'),
      });
      return;
    }

    let finalBillingPeriodRange = { ...billingPeriodRange };

    // If there's a start date but no end date, set the end date to the end of the month.
    if (finalBillingPeriodRange.from && !finalBillingPeriodRange.to) {
      finalBillingPeriodRange.to = endOfMonth(finalBillingPeriodRange.from);
    }

    // Adjust billingPeriod dates to be timezone-neutral date strings (yyyy-MM-dd)
    // The server should interpret these dates using user.timezone.
    const adjustedBillingPeriod = {
      from: finalBillingPeriodRange.from ? format(finalBillingPeriodRange.from, 'yyyy-MM-dd') : undefined,
      to: finalBillingPeriodRange.to ? format(finalBillingPeriodRange.to, 'yyyy-MM-dd') : undefined,
    };

    const invoiceData = {
      ...values,
      billingPeriod: adjustedBillingPeriod,
      events: selectedEvents,
      taxes: taxes.filter((tax) => tax.selected),
      subtotal: getSubtotal(),
      discount: {
        type: discountType,
        value: discountValue,
        scope: discountScope,
        amount: getDiscountAmount(),
      },
      taxAmount: getTaxAmount(),
      additionalFeesTotal: getAdditionalFeesTotal(),
      additionalFees: additionalFees,
      total: getTotal(),
      studentId,
    };
    

    setIsCreatingInvoice(true);

    try {
      const response = await fetch(`${BASE_URL}/api/invoice/create`, {
        method: 'POST',
        headers: {
          'Accept' : 'application/json',
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(invoiceData),
      });

      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }

      const data = await response.json();
      const newInvoiceId = data.invoiceId;
      eventService.clearEventsCache();
      queryClient.clear();

      toast({
        title: translateStr('invoices.invoiceCreated'),
        description: translateStr('invoices.invoiceCreatedDescription'),
      });

      const splitIds: any[] = Array.isArray(data.invoiceIds) ? data.invoiceIds : [];
      if (data.type === 'split' && splitIds.length > 0) {
        navigate('/invoices/created?ids=' + splitIds.join(','));
      } else {
        navigate('/invoices/' + (newInvoiceId ?? splitIds[0]));
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        variant: "destructive",
        title: translateStr('invoices.invoiceCreationFailed'),
        description: translateStr('invoices.invoiceCreationFailedDescription'),
      });
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container py-8 mt-16">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices/billing')}
            className="mb-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {translateStr('common.back')}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CardTitle>
                  <div className="flex items-center gap-3">
                    <User className="h-6 w-6" />
                    <span>
                      {student && `${student.firstname} ${student.lastname}${student.status === "deleted" ? " (" + t('invoices.deleted') + ")" : ""}`}
                    </span>
                  </div>
                  </CardTitle>
                </div>
               {/*<CardDescription>
                  {translateStr('invoices.createInvoice')}
                </CardDescription>*/}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/*<div>
                    <Label htmlFor="category-filter">{translateStr('invoices.filterByCategory')}</Label>
                    <Select 
                      value={filterCategory} 
                      onValueChange={setFilterCategory}
                    >
                      <SelectTrigger id="category-filter">
                        <SelectValue placeholder={translateStr('invoices.allCategories')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{translateStr('invoices.allCategories')}</SelectItem>
                        {categories.map(category => (
                          <SelectItem key={category.id} value={category.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                              {category.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>*/}
                  
                  <div>
                    <Label htmlFor="period-filter">{translateStr('invoices.filterByPeriod')}</Label>
                    <Select 
                      value={filterPeriod} 
                      onValueChange={(value: FilterPeriod) => setFilterPeriod(value)}
                    >
                      <SelectTrigger id="period-filter">
                        <SelectValue placeholder={translateStr('invoices.allTime')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{translateStr('invoices.allTime')}</SelectItem>
                        <SelectItem value="currentMonth">{translateStr('invoices.currentMonth')}</SelectItem>
                        <SelectItem value="currentYear">{translateStr('invoices.currentYear')}</SelectItem>
                        <SelectItem value="custom">{translateStr('invoices.customDateRange')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {filterPeriod === 'custom' && (
                    <div>
                      <Label htmlFor="date-range">{translateStr('invoices.dateRange')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="date-range"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !dateRange.from && "text-muted-foreground"
                            )}
                          >
                            {dateRange.from ? (
                              dateRange.to ? (
                                <>
                                  {format(dateRange.from, "LLL dd, y")} -{" "}
                                  {format(dateRange.to, "LLL dd, y")}
                                </>
                              ) : (
                                format(dateRange.from, "LLL dd, y")
                              )
                            ) : (
                              <span>{translateStr('invoices.pickDateRange')}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            initialFocus
                            mode="range"
                            defaultMonth={dateRange.from}
                            selected={dateRange as RDPDateRange}
                            onSelect={(range) => {
                              setDateRange(range || { from: undefined, to: undefined });
                              setFilterPeriod('custom');
                            }}
                            numberOfMonths={2}
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>

                {Boolean(student?.invoice_split_between_parents) && (
                  <Alert className="mb-4">
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <p>{translateStr('students.invoiceSplitNoticeTitle')}</p>
                        <p>{translateStr('students.invoiceSplitNoticeDescription')}</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/*<div className="flex justify-end mt-4">
                  <Button variant="outline" onClick={resetFilters} className="ml-2">
                    {translateStr('invoices.resetFilters')}
                  </Button>
                </div>*/}

                {studentLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-[200px]" />
                    <Skeleton className="h-4 w-[300px]" />
                  </div>
                ) : student ? (
                  <div></div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {translateStr('invoices.studentNotFound')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.pendingEvents')}</CardTitle>
                <CardDescription>
                  {translateStr('invoices.selectEventsToInvoice')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {eventsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="h-6 w-6" />
                        <Skeleton className="h-12 w-full" />
                      </div>
                    ))}
                  </div>
                ) : filteredEvents.length > 0 ? (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                         <TableHead
                            onClick={handleSortChange}
                            style={{ cursor: 'pointer' }}
                            className="flex items-center gap-1"
                          >
                            {translateStr('invoices.date')}
                            {sortOrder === 'asc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                          </TableHead>
                          {/*<TableHead>{translateStr('invoices.category')}</TableHead>*/}
                          <TableHead>{translateStr('invoices.description')}</TableHead>
                          <TableHead>{translateStr('invoices.duration')}</TableHead>  
                          <TableHead className="text-right">{translateStr('invoices.amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <Checkbox
                                checked={event.selected}
                                onCheckedChange={() => handleToggleEvent(event.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {formatDate(event.date)}
                              </div>
                            </TableCell>
                            {/*<TableCell>
                              <Badge
                                variant="outline"
                                style={{
                                  backgroundColor: `${event.categoryColor}20`,
                                  color: event.categoryColor,
                                  borderColor: event.categoryColor
                                }}
                              >
                                {event.category}
                              </Badge>
                            </TableCell>
                            */}
                             <TableCell className="max-w-[270px]">

                              {!event.fullDay ? event.category : ''}                      

                              {!event.fullDay && isDisplayStudentCount ? (
                               <span className="text-xs">
                              {event.studentCount > 1 ? ' (' + t('common.with') : ''} {event.studentCount > 1 ? event.studentCount + ' ' : ' ('} 
                              {event.studentCount > 0 && (
                                event.studentCount > 1
                                  ? t('students.students') + ')'
                                  : t('agenda.privateLesson') + ')'
                              )}
                              </span>
                              ) : (''
                              )}

                              {event.title === "Lesson" ? (
                                  ''
                                ) : (
                                  <>
                                    {(event.title === "Event" || event.fullDay) && (
                                      <span className="badge bg-gray-100 text-xs text-gray-400 font-medium p-1 rounded-full">
                                        {t('agenda.Event')}
                                      </span>
                                    )}
                                    {event.title !== "Event" && (
                                      <>
                                        {' '}
                                        <p>{event.title}</p>
                                      </>
                                    )}
                                    <p className="text-gray-700 text-sm">
                                    {event.notes ? ' ' + event.notes : ''}
                                    </p>
                                  </>
                                )}
                                
                            </TableCell>
                            <TableCell>{!event.fullDay ? formatDuration(event.duration) : '-'}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(event.price)}
                              {event.extraCharge > 0 && (
                                <>
                                <br/>
                                  <span className="text-xs">
                                    + {formatCurrency(event.extraCharge)} {translateStr('invoices.extraCharges')}
                                  </span>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {translateStr('invoices.noPendingEvents')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-5">
            <Card>
              <CardHeader>
                <div className="flex items-center">
                  <CardTitle>
                    {translateStr('invoices.parameters')}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form className="space-y-4 ">
                    <FormField
                      control={form.control}
                      name="invoiceNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.invoiceNumber')}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />


                    <div>
                      <Label htmlFor="billing-period-range">
                        <div className="flex items-center">
                          {translateStr('invoices.billingPeriod')}
                          <TooltipProvider>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground ml-2 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('invoices.billingPeriodDateTooltip', "The billing period will be used in the annual billing report")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="billing-period-range"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal mt-2",
                              !billingPeriodRange.from && "text-muted-foreground"
                            )}
                          >
                            {billingPeriodRange.from ? (
                              billingPeriodRange.to ? (
                                <>
                                  {format(billingPeriodRange.from, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')} -{" "}
                                  {format(billingPeriodRange.to, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')}
                                </>
                              ) : (
                                format(billingPeriodRange.from, "LLL dd, y")
                              )
                            ) : (
                              <span>{translateStr('invoices.pickDateRange')}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            initialFocus
                            mode="range"
                            month={billingCalendarMonth}
                            onMonthChange={setBillingCalendarMonth}
                            selected={billingPeriodRange as RDPDateRange}
                            onSelect={(range) => {
                                const newRange = range || { from: undefined, to: undefined };
                                // if a range is selected that spans months (shift-click), invalidate it
                                if (newRange.from && newRange.to && !isSameMonth(newRange.from, newRange.to)) {
                                    // just take the start date
                                    setBillingPeriodRange({ from: newRange.from, to: undefined });
                                    setBillingCalendarMonth(newRange.from);
                                } else {
                                    setBillingPeriodRange(newRange);
                                    if(newRange.from) {
                                        setBillingCalendarMonth(newRange.from);
                                    }
                                }
                            }}
                            disabled={(date) => {
                                // if a start date is selected, disable dates in other months
                                if (billingPeriodRange.from && !billingPeriodRange.to) {
                                    return !isSameMonth(date, billingPeriodRange.from)
                                }
                                return false;
                            }}
                            numberOfMonths={1}
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>



                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="invoiceDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                                {translateStr('invoices.issueDate')}   
                            </FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
                                    ) : (
                                      <span>{translateStr('common.selectDate')}</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value ? new Date(field.value) : undefined}
                                  onSelect={(date) => {
                                    const formattedDate = format(date!, 'yyyy-MM-dd');
                                    field.onChange(new Date(formattedDate));
                                    setInvoiceDateMonth(new Date(date!));
                                  }}
                                  month={invoiceDateMonth}
                                  onMonthChange={setInvoiceDateMonth}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                />
                              </PopoverContent>
                            </Popover>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="dueDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translateStr('invoices.dueDate')}</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy')
                                    ) : (
                                      <span>{translateStr('common.selectDate')}</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value ? new Date(field.value) : undefined}
                                  onSelect={(date) => {
                                    const formattedDate = format(date!, 'yyyy-MM-dd');
                                    field.onChange(new Date(formattedDate));
                                    setDueDateMonth(new Date(date!));
                                  }}
                                  month={dueDateMonth}
                                  onMonthChange={setDueDateMonth}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                  disabled={(date) => date < new Date(form.getValues().invoiceDate)}
                                />
                              </PopoverContent>
                            </Popover>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="note"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.notes')}</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={translateStr('invoices.notesPlaceholder')}
                              className="resize-none h-20"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.taxes')}</CardTitle>
              </CardHeader>
              <CardContent>
                {taxesLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-6 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {taxes.length === 0 ? (
                      <p className="text-sm font-medium">No taxes</p>
                    ) : (
                      taxes.map((tax) => (
                        <div key={tax.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`tax-${tax.id}`}
                            checked={tax.selected}
                            onCheckedChange={() => handleToggleTax(tax.id)}
                          />
                          <label
                            htmlFor={`tax-${tax.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {tax.name} ({tax.rate}%)
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.discount')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-[2fr_1fr] gap-1 items-center">
                    {/* Select */}
                    <Select
                      value={discountType}
                      onValueChange={(value: 'none' | 'percentage' | 'amount') => {
                        setDiscountType(value);
                        if (value === 'none') setDiscountValue(0);
                      }}
                    >
                      <SelectTrigger className="relative flex items-center w-full">
                        <SelectValue
                          className={`ml-${discountType !== 'none' ? '6' : '2'}`}
                          placeholder={translateStr('invoices.selectDiscountType')}
                        />
                      </SelectTrigger>

                      <SelectContent className="w-full">
                        <SelectItem value="none">{translateStr('invoices.noDiscount')}</SelectItem>
                        <SelectItem value="percentage">{translateStr('invoices.percentageDiscount')}</SelectItem>
                        <SelectItem value="amount">{translateStr('invoices.amountDiscount')}</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Input */}
                    {discountType !== 'none' && (
                      <div className="relative w-full">
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={discountInput}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setDiscountInput(raw);

                          const num = parseFloat(raw.replace(',', '.'));
                          setDiscountValue(isNaN(num) ? 0 : num);
                        }}
                        className="pr-12 w-full" 
                      />

                    {isMobile ? (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-muted-foreground">
                        {discountType === 'percentage' ? '%' : user.currency_code || '$'}
                      </div>
                    ) : (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-muted-foreground">
                        {discountType === 'percentage' ? '%' : user.currency_code || '$'}
                      </div>
                    )}
            </div>
        )}
      </div>

      {discountType !== 'none' && (
        <div className="mt-4">
          <Label htmlFor="discount-scope">{translateStr('invoices.discountScope')}</Label>
          <Select
            value={discountScope}
            onValueChange={(value: 'all' | 'lessons' | 'events') => setDiscountScope(value)}
          >
            <SelectTrigger id="discount-scope">
              <SelectValue placeholder={translateStr('invoices.discountScope')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{translateStr('invoices.discountScopeAll')}</SelectItem>
              <SelectItem value="lessons">{translateStr('invoices.discountScopeLessons')}</SelectItem>
              <SelectItem value="events">{translateStr('invoices.discountScopeEvents')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  </CardContent>
</Card>


            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.additionalFees')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {additionalFees.map((fee, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={fee.description}
                        onChange={(e) => handleAdditionalFeeChange(index, 'description', e.target.value)}
                        placeholder={translateStr('invoices.feeDescriptionPlaceholder')}
                        className="flex-1"
                      />
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={fee.amountInput ?? ''} 
                        onChange={(e) => handleAdditionalFeeChange(index, 'amount', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder={translateStr('invoices.amount')}
                        className="w-24"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAdditionalFee(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addAdditionalFee}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {translateStr('invoices.addFee')}
                  </Button>
                </div>
              </CardContent>
            </Card>


            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">{taxeType === 1 ? translateStr('invoices.subtotalHT') : translateStr('invoices.subtotal')}:</span>
                    <span className="text-sm font-medium">{formatCurrency(taxeType === 1 ? getSubtotal() - getTaxAmount() : getSubtotal())}</span>
                  </div>

                  {discountType !== 'none' && discountValue > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span className="text-sm">{translateStr('invoices.discount')}:</span>
                      <span className="text-sm font-medium">-{formatCurrency(getDiscountAmount())}</span>
                    </div>
                  )}

                  {getSubtotalWithExtra() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm">{translateStr('invoices.extraCharges')}:</span>
                      <span className="text-sm font-medium">{formatCurrency(getSubtotalWithExtra())}</span>
                    </div>
                  )}

                  {taxes.some(tax => tax.selected) && (
                    <div className="flex justify-between">
                      <span className="text-sm">{taxeType === 1 ? translateStr('invoices.taxIncluded') : translateStr('invoices.tax')}:</span>
                      <span className="text-sm font-medium">{formatCurrency(getTaxAmount())}</span>
                    </div>
                  )}


                  {additionalFees.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm">{translateStr('invoices.additionalFees')}:</span>
                      <span className="text-sm font-medium">{formatCurrency(getAdditionalFeesTotal())}</span>
                    </div>
                  )}
          

                  <Separator />

                  <div className="flex justify-between pt-2">
                    <span className="text-base font-bold">{taxeType === 1 ? translateStr('invoices.totalTTC') : translateStr('invoices.total')}:</span>
                    <span className="text-base font-bold">{formatCurrency(getTotal())}</span>
                  </div>

                  <div className="mt-6">
                    <Button 
                      className="w-full" 
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={isCreatingInvoice}
                    >
                      {isCreatingInvoice ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          {translateStr('common.loading')}
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          {translateStr('invoices.buttons.create')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InvoiceCreate;
